import { useEffect, useMemo, useRef, useState } from "react";

import { usePlaidLink } from "react-plaid-link";
import { useHistory } from "react-router";

import { toastError } from "@alpacahq/ui/old";

import { useGetAllRelationships, useGetOwnerDetails } from "~/v2/api/hooks";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { useGetPaths } from "../../useGetPath";
import type { PlaidStatus } from "../PlaidStatusPanel.pres";
import {
  clearPlaidOAuthSession,
  isPlaidOAuthSessionValid,
  type PlaidOAuthSession,
  readPlaidOAuthSession,
} from "../plaidOAuthSession";
import type { ResumeUnavailableReason } from "../plaidTelemetry";
import {
  trackResumeAutoOpened,
  trackResumeDetected,
  trackResumeTokenExpired,
  trackResumeUnavailable,
} from "../plaidTelemetry";
import { createPlaidEventHandlers } from "../usePlaidLinkEvents";
import { useSubmitPlaidRelationship } from "../useSubmitPlaidRelationship";

type SessionState =
  | { status: "pending"; session: null }
  | { status: "valid"; session: PlaidOAuthSession }
  | { status: "invalid"; session: PlaidOAuthSession | null };

/**
 * Plaid OAuth-resume page controller. Single-purpose: the user has returned
 * from the bank's hosted OAuth flow via a full-page redirect to our
 * redirect_uri with `?oauth_state_id=...`. To resume the SAME Link session we
 * re-init usePlaidLink with the ORIGINAL persisted link_token plus
 * receivedRedirectUri (we never fetch a fresh token here — a new link_token
 * cannot resume the existing OAuth session).
 */
export const usePlaidOAuthReturnController = () => {
  const history = useHistory();

  // Snapshot the return URL (full href incl. oauth_state_id) ONCE at first
  // render, before any history.replace can strip the query string. useRef
  // guarantees compute-exactly-once — critical because a recompute after the URL
  // is cleaned would null out oauth_state_id and silently break the resume.
  const oauthReturnRef = useRef({
    href: window?.location?.href ?? "",
    search: window?.location?.search ?? "",
    oauthStateId:
      new URLSearchParams(window?.location?.search ?? "").get(
        "oauth_state_id",
      ) ?? "",
  });

  const [status, setStatus] = useState<PlaidStatus>("idle");
  const [sessionState, setSessionState] = useState<SessionState>({
    status: "pending",
    session: null,
  });

  const { currentAccount } = useAccountContext({ liveOnly: true });
  const accountID = currentAccount?.id ?? "";

  const { details } = useGetOwnerDetails();

  const { ach } = useGetAllRelationships({
    account: currentAccount,
    countryOfTaxResidence: details?.country_of_tax_residence || "",
  });
  const achRelationship = ach?.relationships?.at(0);

  // Ref keeps the relationship current inside usePlaidLink's stale onSuccess.
  const achRelationshipRef = useRef(achRelationship);
  useEffect(() => {
    achRelationshipRef.current = achRelationship;
  }, [achRelationship]);

  // Resolve the persisted session exactly once, after accountID is known.
  // Validity requires both an oauth_state_id on the return URL and an unexpired
  // session for this account.
  useEffect(() => {
    if (!accountID || sessionState.status !== "pending") {
      return;
    }
    const session = readPlaidOAuthSession(accountID);
    const valid =
      !!oauthReturnRef.current.oauthStateId &&
      isPlaidOAuthSessionValid(session, accountID);
    setSessionState(
      valid ? { status: "valid", session } : { status: "invalid", session },
    );
  }, [accountID, sessionState.status]);

  // Derive deposit/withdraw targets from the PERSISTED search (the original
  // pre-redirect intent), NOT the live oauth_state_id URL — this is what fixes
  // the stale-paths bug from the coupled page.
  const paths = useGetPaths("ACH", sessionState.session?.search);

  const isVerifyingMicroDeposit = useMemo(() => {
    const params = new URLSearchParams(sessionState.session?.search ?? "");
    return params.get("micro_deposit_verification_required") === "true";
  }, [sessionState.session]);

  // On OAuth resume reuse the SAME persisted link_token, read synchronously so
  // it rides in alongside receivedRedirectUri. react-plaid-link v4.1.1's init
  // effect keys on `token` (not receivedRedirectUri), so token flipping
  // null->value as the session resolves to "valid" is what (re)initializes Link
  // with the redirect URI in a single mount.
  const linkToken =
    sessionState.status === "valid" ? sessionState.session.link_token : null;

  const { handlePlaidSuccess } = useSubmitPlaidRelationship({
    accountID,
    getCurrentRelationship: () => achRelationshipRef.current,
    onStatusChange: setStatus,
    clearSession: () => clearPlaidOAuthSession(accountID),
    onCompleted: () => {
      setTimeout(() => {
        history.push(paths.next);
      }, 1000);
    },
  });

  const { onEvent, onExit } = createPlaidEventHandlers({
    accountID,
    // Always drop the in-flight resume session on exit so a stale token is never
    // reused on a later, unrelated attempt.
    beforeExit: () => clearPlaidOAuthSession(accountID),
    onExitClean: () => history.push(paths.back),
    onExitError: () => history.push(paths.back),
    onInvalidLinkToken: (metadata) => {
      // Expired/invalid persisted token on OAuth return: emit the funnel signal,
      // (session already cleared in beforeExit) and send the user to the fresh
      // page to start over, preserving the original ?transfer= context.
      trackResumeTokenExpired({ link_session_id: metadata.link_session_id });
      toastError("Your bank-linking session expired. Please try again.");
      history.replace(
        `${Path.ROUTE_FUNDING_LINK_ACH}${sessionState.session?.search ?? ""}`,
      );
    },
  });

  const { open: openPlaid, ready: isPlaidReady } = usePlaidLink({
    token: linkToken,
    // Required on the OAuth return so Plaid associates this init with the
    // earlier Link session. The pristine href is captured in oauthReturnRef.
    receivedRedirectUri: oauthReturnRef.current.href,
    onSuccess: handlePlaidSuccess,
    onExit,
    onEvent,
    userLegalName: currentAccount?.name,
    userEmailAddress: currentAccount?.email,
  });

  // On a valid resume: emit the funnel entry signal once and restore the
  // original ?transfer= context (dropping oauth_state_id from the visible URL).
  // age_ms = how long the user spent in the bank's OAuth flow before returning;
  // high values explain expired-token failures on slow mobile redirects.
  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (sessionState.status !== "valid" || hasRestoredRef.current) {
      return;
    }
    hasRestoredRef.current = true;
    trackResumeDetected({
      age_ms: Date.now() - sessionState.session.savedAt,
      had_search: !!sessionState.session.search,
    });
    history.replace(
      `${window.location.pathname}${sessionState.session.search ?? ""}`,
    );
  }, [sessionState, history]);

  // Auto-open Link once on a valid resume so the user continues without a click.
  // The !!linkToken gate is defensive: react-plaid-link v4.1.1 reports `ready`
  // as soon as Link initializes with receivedRedirectUri even if token is
  // momentarily null, so we only auto-open once the resume token is present.
  const hasAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (
      sessionState.status === "valid" &&
      linkToken &&
      isPlaidReady &&
      !hasAutoOpenedRef.current
    ) {
      hasAutoOpenedRef.current = true;
      trackResumeAutoOpened();
      setStatus("plaid_open");
      openPlaid();
    }
  }, [sessionState.status, linkToken, isPlaidReady, openPlaid]);

  // Unresumable landing on the OAuth-return page. Classify why, then send the
  // user back to the fresh page preserving any non-oauth params.
  const hasHandledOrphanRef = useRef(false);
  useEffect(() => {
    if (sessionState.status !== "invalid" || hasHandledOrphanRef.current) {
      return;
    }
    hasHandledOrphanRef.current = true;
    const hasOAuthStateId = !!oauthReturnRef.current.oauthStateId;
    // Distinguish a genuine expired OAuth return from errant direct navigation:
    //  - "no_oauth_state_id": not an OAuth return at all (e.g. someone opened
    //    /mobile/plaid/oauth-page.html directly). A still-valid persisted session
    //    may belong to a real resume that arrives later, so we must NOT destroy
    //    it here.
    //  - "expired_or_invalid": returned with oauth_state_id but the persisted
    //    session failed validation (TTL/expiration/account mismatch). Honor the
    //    single-use intent and drop it so a dead token can't be replayed.
    //  - "no_session": returned with oauth_state_id but nothing was persisted
    //    (storage blocked at save time, or a different device/account).
    let reason: ResumeUnavailableReason;
    if (!hasOAuthStateId) {
      reason = "no_oauth_state_id";
    } else if (sessionState.session) {
      reason = "expired_or_invalid";
      clearPlaidOAuthSession(accountID);
    } else {
      reason = "no_session";
    }
    trackResumeUnavailable({ reason, oauth_state_id_present: hasOAuthStateId });
    // Only surface the "session expired" toast for an actual OAuth return;
    // direct navigation isn't an expiry and shouldn't show a scary message.
    if (hasOAuthStateId) {
      toastError("Your bank-linking session expired. Please start again.");
    }
    const params = new URLSearchParams(oauthReturnRef.current.search);
    params.delete("oauth_state_id");
    const query = params.toString();
    history.replace(
      `${Path.ROUTE_FUNDING_LINK_ACH}${query ? `?${query}` : ""}`,
    );
  }, [sessionState, accountID, history]);

  const onOpenPlaidClicked = () => {
    setStatus("plaid_open");
    openPlaid();
  };

  const onBackClicked = () => {
    history.push(paths.back);
  };

  return {
    status,
    isPlaidReady,
    isVerifyingMicroDeposit,
    onOpenPlaid: onOpenPlaidClicked,
    onBack: onBackClicked,
    // Connector gating: spinner until the account loads and the session
    // resolves; "invalid" is mid-redirect to the fresh page.
    isResolving:
      !currentAccount ||
      !details ||
      ach.isLoading ||
      sessionState.status === "pending",
    isUnavailable: sessionState.status === "invalid",
  };
};

export type UsePlaidOAuthReturnControllerReturn = ReturnType<
  typeof usePlaidOAuthReturnController
>;
